import {
	getInvoicesIdDownloadPath,
	type InvoiceDetailResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { downloadFile } from "Infrastructure/Utilities/downloadFile";
import { useRegenerateInvoice } from "Order/Modules/Invoice/Api/Mutation/useRegenerateInvoiceMutation";
import { PartnerPaths } from "Partner/Routes/Routing";
import {
	ArrowDropDown,
	Download,
	Link as LinkIconMui,
	Refresh,
} from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";

type InvoiceActionsProps = {
	showRegenerateInvoiceButton?: boolean;
	invoice: InvoiceDetailResponseDto;
};

const invoiceResource = Resources.base.invoice.detail;

export function InvoiceActions({
	showRegenerateInvoiceButton,
	invoice,
}: InvoiceActionsProps) {
	const { t } = useResource();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const navigate = useNavigate();

	const { mutate: regenerateInvoice, isPending: isPendingRegenerateInvoice } =
		useRegenerateInvoice(invoice.order!.id);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleRegenerateInvoice = () => {
		regenerateInvoice({});
	};

	const handleNavigateToPartner = () => {
		invoice.partnerId && navigate(PartnerPaths.Detail.path(invoice?.partnerId));
	};

	return (
		<>
			<Button
				onClick={handleMenuOpen}
				aria-label="Invoice actions"
				endIcon={<ArrowDropDown />}
			>
				{t(invoiceResource.actions)}
			</Button>

			<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
				<MenuItem
					onClick={() => {
						downloadFile(getInvoicesIdDownloadPath(invoice.id));
						handleMenuClose();
					}}
					sx={{
						display: "flex",
						gap: 1,
					}}
				>
					<Download fontSize="small" />
					{t(invoiceResource.downloadInvoice)}
				</MenuItem>

				{showRegenerateInvoiceButton && (
					<MenuItem
						onClick={() => {
							handleRegenerateInvoice();
							handleMenuClose();
						}}
						disabled={!invoice.order?.id || isPendingRegenerateInvoice}
						sx={{
							display: "flex",
							gap: 1,
						}}
					>
						<Refresh fontSize="small" />
						{t(invoiceResource.regenerateInvoice)}
					</MenuItem>
				)}

				<MenuItem
					onClick={() => {
						handleNavigateToPartner();
						handleMenuClose();
					}}
					disabled={!invoice?.partnerId}
					sx={{
						display: "flex",
						gap: 1,
					}}
				>
					<LinkIconMui fontSize="small" />
					{t(invoiceResource.partner)}
				</MenuItem>
			</Menu>
		</>
	);
}
